import React from 'react';
import styled from 'styled-components/macro';

import EntityItem from 'components/base/EntityItem/EntityItem';
import Box from 'components/elements/Box';
import AddToListButton from 'components/base/AddToListButton';
import { IEntityInfo } from 'pages/Search';
import { EntityTypes } from 'context/entityContext';
import { entityTypeToApiType } from 'helpers/mapEntityTypes';

export const StyledEntityItem = styled(EntityItem)`
  padding: ${({ theme }): string => theme.spacing.small};
  margin: 0 -${({ theme }): string => theme.spacing.small};

  @media screen and (min-width: 43em) {
    margin: 0;
    padding: ${({ theme }): string => theme.spacing.small} 0;
  }
`;

export const ResultItemWrapper = styled(Box)`
  border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};

  &:hover,
  &:focus {
    background: ${({ theme }): string => theme.Sand};
  }

  &:active {
    background: ${({ theme }): string => theme.LightGrey};
  }
`;

export interface IEntity {
  id: string;
  type: string;
  displayName: string;
  primaryArtist: {
    id: string;
    type: string;
    displayName: string;
  };
  releaseDate: number;
  thumbnailUrl: string;
  grouped_track_count: number;
  grouped_album_count: number;
  artist_count: number;
  is_group: boolean;
  parent_group_id: string;
  group_child_count: number;
  data: any;
  lastVisit: string;
  visitAmount?: number;
  productType?: string;
  isrc_list: string[];
  isCompilation?: boolean;
}

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

interface IResultListData {
  /** The data returned from the API */
  data: IEntity[];
  onAddToListClick: (entityInfo: IEntityInfo) => void;
  onOpenEntity?: (entityInfo: IEntity) => void;
}

/** ResultsList conditionally displays the data it is passed */
const ResultsList: React.FC<IResultListData> = (props) => {
  const { data, onAddToListClick, onOpenEntity = noop } = props;

  return (
    <ul data-pw="result-list-section">
      {data.map((entity: IEntity): any => {
        /** Sometimes the Search API returns a null value */
        if (!entity) {
          return null;
        }

        return (
          <React.Fragment key={entity.id}>
            <li>
              <ResultItemWrapper
                display="flex"
                justifyContent="center"
                alignItems="center"
                alignContent="stretch"
              >
                <Box width="100%" onClick={(): void => onOpenEntity(entity)}>
                  <StyledEntityItem
                    entityId={entity.id}
                    entityType={entity.type}
                    displayName={entity.displayName}
                    primaryArtist={entity.primaryArtist}
                    thumbnailUrl={entity.thumbnailUrl}
                    releaseDate={entity.releaseDate}
                    isGroup={entity.is_group}
                    parentEntityId={entity.parent_group_id}
                    groupChildCount={entity.group_child_count}
                    releaseCount={entity.grouped_album_count}
                    artistCount={entity.artist_count}
                    trackCount={entity.grouped_track_count}
                    entityData={entity.data}
                    artworkHasBackground={true}
                    isLink={true}
                    preserveSearch={true}
                    releaseType={
                      entity?.productType ? entity?.productType : null
                    }
                    isrc_list={entity?.isrc_list}
                    releaseCompilation={
                      entity.isCompilation ? 'COMP' : undefined
                    }
                  />
                </Box>
                {entity.type !== 'label' && (
                  <Box>
                    <AddToListButton
                      onClick={(): void =>
                        onAddToListClick({
                          entityId: entity.id,
                          entityName: entity.displayName,
                          apiEntityType: entityTypeToApiType(
                            entity.type as any
                          ) as EntityTypes,
                        })
                      }
                    />
                  </Box>
                )}
              </ResultItemWrapper>
            </li>
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export default ResultsList;
